import React, { useEffect, useState, useRef } from 'react';
import anime from 'animejs/lib/anime.es.js';
import { Box, Grid, useMediaQuery } from '@mui/material';
import gsap from 'gsap';
import Garland from './garlandSvg'; // Import the GarlandSvg component
import GarlandL from './garlandSvgL'; // Import the GarlandSvg component
import LampIcon from './lampIconSvg';
import '../App.css';

const AnimatedText = ({ text = 'i love lamp', onAnimationDone }) => {
  // State to hold the width of the child elements
  const [childWidth, setChildWidth] = useState('auto');
  const containerRef = useRef();
  const lampIconRef = useRef(null); // Create a ref for our .lamp-icon
  const [adjustedWidth, setAdjustedWidth] = useState(null);
  const lineRef = useRef(null);

  // Determines if the screen size is small (e.g., mobile devices)
  const isSmallScreen = useMediaQuery('(max-width:600px)');

  useEffect(() => {
    if (containerRef.current) {
      let totalWidth = 0;
      containerRef.current.childNodes.forEach(child => {
        totalWidth += child.offsetWidth;
      });
      setChildWidth(`${totalWidth}px`);
    }
    const calculateAdjustedWidth = () => {
      if (containerRef.current) {
        const ParentRect = containerRef.current.getBoundingClientRect();
        const calculatedChildWidth = ParentRect.width;

        // Calculate full available width as 80vw and adjust based on child width
        const fullWidth = window.innerWidth * 0.78;
        const adjusted = Math.floor((fullWidth-calculatedChildWidth) / 10) * 10; // Nearest multiple of 10
        setAdjustedWidth(adjusted);
        console.log('adjustedWidth', adjusted);
      }
    };

    const timeoutId = setTimeout(calculateAdjustedWidth, 50); // Delay calculating width
    window.addEventListener("resize", calculateAdjustedWidth); // Recalculate on resize

    return () => {
      clearTimeout(timeoutId); // Clear timeout if component unmounts
      window.removeEventListener("resize", calculateAdjustedWidth);
    };
  }, []);
  
  console.log('adjustedWidth', adjustedWidth);

  // TITLE ANIMATION
  useEffect(() => {
    const lampIconGSAP = () => {
      // add bouncing logic for logo
      if (lampIconRef.current && !lampIconRef.current.classList.contains("animated")) {
        gsap.timeline({ onComplete: () => {
          lampIconRef.current.classList.add("animated"); // Mark as animated
          }
        })
        .set(".lamp-icon", { scale: 0 }) // Starting condition, immediate
        .to(".lamp-icon", { scale: 0, duration: 0.1 }) // Ensure immediate scale to 0
        .set(".lamp-icon-like", { scale: 0 }) // Starting condition, immediate
        .to(".lamp-icon-like", { scale: 0, duration: 0.1 }) // Ensure immediate scale to 0
        .to(".lamp-icon", {
          scale: 1.2,
          duration: 1.5,
          rotate: 30,
          opacity: 1,
          ease: "elastic.out(2, 0.2)",
          delay: 1.65})// Adjust based on your animated text duration
        .to(".lamp-icon-like", {
          scale: 1.2,
          duration: 1.2,
          opacity: 1,
          ease: "elastic.out(2, 0.2)",
          delay: -1.1, // Adjust based on your animated text duration
        });
      }
    };
    // Immediately invoke the handleScroll function to apply styles based on the initial scroll position.
    lampIconGSAP();
  }, []);

  // TEXT + ICON ANIMATION
  useEffect(() => {
    const cups1 = [];
    for (let i = 8; i >= 1; i--) {
      cups1.push(`#c${i}`);
    }

    const cups2 = [];
    for (let i = 9; i <= 16; i++) {
      cups2.push(`#c${i}`);
    }

    const animationDelay = isSmallScreen ? "-=1000" : "-=2500";

    let textWrapper = document.querySelector('.ml9 .letters');
    if (textWrapper && !textWrapper.innerHTML.includes("<span class='letter'>")) {
      textWrapper.innerHTML = textWrapper.textContent.replace(/\S/g, "<span class='letter'>$&</span>");

      anime.timeline({
        loop: false, // Ensure the animation does not loop
      })
      .add({
        targets: '#animatedLine',
        strokeDashoffset: [anime.setDashoffset, 0],
        easing: 'easeInExpo',
        opacity: [1,1],
        duration: 1000,
      })
      .add({
        targets: '.ml9',
        opacity: [0,1],
        duration: 0, // adjust as needed
      })
      .add({
        targets: '.ml9 .letter',
        opacity: [0,1],
        scale: [0, 1],
        duration: 1500,
        elasticity: 600,
        delay: (el, i) => 500+45 * (i + 1)
      },"-=400")
      .add({
        targets: ['#R', '#L'],
        opacity: [0,1],
        duration: 0,
        loop: false,
        complete: onAnimationDone 
      })
      .add({
        targets: '#mR',
        strokeDashoffset: [anime.setDashoffset, 0],
        easing: 'easeOutQuart',
        duration: 500,
        loop: false,
      })
      .add({
        targets: '#mL',
        strokeDashoffset: [anime.setDashoffset, 0],
        easing: 'easeOutQuart',
        duration: 500,
        loop: false
      },"-=500")
      .add({
        targets: cups1,
        scale: { value:[0,1], easing:'easeOutElastic(1.3, 0.4)', duration: 2000, delay: anime.stagger(500/8)},
        transformOrigin: '50% 50%',
        opacity: 1,
      },"-=500")
      .add({
        targets: cups2,
        scale: { value:[0,1], easing:'easeOutElastic(1.3, 0.4)', duration: 2000, delay: anime.stagger(500/8)},
        transformOrigin: '50% 50%',
        opacity: 1// Invoking the passed function upon animation completion
      },"-=2500")
      .add({
        targets: ['#dotLine'],
        easing: 'easeOutQuart',
        duration: 1000,
        stroke: ["#d4aa2d", "#000000"],
      },"-=2500")
      .add({
        targets: ['.ml9 .letter'],
        easing: 'easeOutQuart',
        duration: 1000,
        color: ["#d4aa2d", "#000000"],
      },animationDelay)
      .add({
        targets: ['#circleBg'],
        easing: 'easeOutQuart',
        duration: 1000,
        fill: ["#d4aa2d", "#000000"],
      },animationDelay)
      .add({
        targets: ['#lamp132'],
        easing: 'easeOutQuart',
        duration: 1000,
        fill: ["#000000", "#d4aa2d"],
      },animationDelay)
    }
  }, [text, onAnimationDone, isSmallScreen]);

  return (
    <>
        <Grid container
          sx={{ position: 'relative', display: 'flex', flexDirection: 'row', alignItems: 'center', alignContent: 'flex-end', width: '100%', paddingTop: '2rem' }}>
          {!isSmallScreen && (
            <Grid item ref={lineRef}  xs sx={{ position: 'relative', display: 'flex', flexGrow: 1 }}>
              <Box sx={{ position: 'absolute', width:adjustedWidth ? `${adjustedWidth}px` : '100%', top: '0.5vh', left: '0' }}>
                <Garland id="Garland" style={{ width: '100%', opacity: '0' }} />
              </Box>
              <Box sx={{ position: 'absolute', width:adjustedWidth ? `${adjustedWidth}px` : '100%', top: '0.5vh', left: '0', zIndex: "-1" }}>
                <GarlandL id="GarlandL" style={{ width: '100%', opacity: '0' }} />
              </Box>
              <svg id='line-svg' width={adjustedWidth ? `${adjustedWidth}px` : '100%'}  height="20">
                <defs>
                  <mask id="anim-mask" maskUnits="userSpaceOnUse" x="0" y="5" width="100%" height="20">
                    <line id="animatedLine" x1="0" y1="5" x2="1000%" y2="5" stroke="white" strokeWidth="15" opacity={0} />
                  </mask>
                </defs>
                <line id="dotLine" x1="0" y1="5" x2="1000%" y2="5" stroke="white" strokeWidth="15" strokeDasharray="8,2" mask="url(#anim-mask)" />
              </svg>
            </Grid>
          )}
          <Grid item ref={containerRef}
            sx={{ position: 'relative', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', marginRight: '2px', width: childWidth * 0.8}}>
            <h1 className="ml9" style={{ opacity: 0, marginLeft: '.5rem' }}>
              <span className="text-wrapper">
                <span className="letters">{text}</span>
              </span>
            </h1>
            <Box sx={{ position: 'relative', marginLeft: '.5rem', alignItems: 'center', display: 'flex' }}>
              <Box sx={{ position: 'relative', zIndex: '0', top: 0, left: 0 }}>
                <LampIcon ref={lampIconRef} alt="Lamp Icon" className='lamp-icon' style={{ opacity: 0 }} />
              </Box>
              <Box sx={{ position: 'absolute', zIndex: '1', top: 0, left: 0 }}>
                <img src={`${process.env.PUBLIC_URL}/lamp-icon-like.svg`} alt="Lamp Icon" className='lamp-icon-like' style={{ opacity: 0 }} />
              </Box>
            </Box>
          </Grid>
        </Grid>
    </>
  );
};

export default AnimatedText;